import { useInfiniteQuery } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { METHOD, sendJSONRequest } from '@/state/http-request.helpers.ts';
import type { GetContentDto } from '@/types/dtos/content/response/get-content.dto.ts';

export const GET_ORGANIZATION_CONTENT_QUERY_KEY = 'get-organization-content';
const PAGE_SIZE = '20';

export const organizationContentQueryKey = (organizationId: string | undefined) =>
  [GET_ORGANIZATION_CONTENT_QUERY_KEY, organizationId] as const;

export const useOrganizationContentQuery = (organizationId: string | undefined) => {
  const { getToken } = useAuth();

  return useInfiniteQuery({
    queryKey: organizationContentQueryKey(organizationId),
    queryFn: async ({ pageParam }) => {
      if (!organizationId) {
        throw new Error('Organization ID is required to load organization content.');
      }
      const token = await getToken();
      return getOrganizationContent({ organizationId, pageParam, token: token ?? '' });
    },
    enabled: Boolean(organizationId),
    initialPageParam: '',
    getNextPageParam: (lastPage) => lastPage.pagination.nextCursor,
  });
};

const getOrganizationContent = async ({
  organizationId,
  pageParam,
  token,
}: {
  organizationId: string;
  pageParam: string;
  token: string;
}): Promise<GetContentDto> => {
  const searchParams = new URLSearchParams({ limit: PAGE_SIZE });
  if (pageParam !== '') {
    searchParams.append('cursor', pageParam);
  }
  const url = `${import.meta.env.VITE_API_URL}/content/organization/${organizationId}?${searchParams.toString()}`;
  return sendJSONRequest({ url, method: METHOD.GET, token });
};
